import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Plus, Trash2, X, Key, ShieldCheck } from "lucide-react";
import { toast, Toaster } from "sonner";

const blank = { email: "", password: "", name: "", phone: "" };

export default function AdminEditors() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [f, setF] = useState(blank);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/editors");
            setItems(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load editors");
        }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!f.name || !f.email) return toast.error("Name and email are required");
        try {
            if (editing === "new") {
                if (!f.password || f.password.length < 6) return toast.error("Password must be at least 6 characters");
                await api.post("/admin/editors", { email: f.email, name: f.name, phone: f.phone, password: f.password });
                toast.success("Editor account created");
            } else {
                await api.put(`/admin/editors/${editing}`, { name: f.name, phone: f.phone });
                toast.success("Editor updated");
            }
            setEditing(null); setF(blank); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const del = async (id) => {
        if (!window.confirm("Delete this editor account?")) return;
        try { await api.delete(`/admin/editors/${id}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const resetPw = async (id) => {
        const pw = window.prompt("New password (min 6 characters):");
        if (!pw) return;
        if (pw.length < 6) return toast.error("Password too short");
        try { await api.put(`/admin/editors/${id}/password`, { new_password: pw }); toast.success("Password reset"); }
        catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const toggleActive = async (e) => {
        try { await api.put(`/admin/editors/${e.id}`, { active: !e.active }); load(); }
        catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    };

    return (
        <div data-testid="admin-editors">
            <Toaster position="top-center" />
            <div className="flex justify-between items-center mb-8">
                <div>
                    <div className="label-eyebrow text-sky-700">Access · admin only</div>
                    <h1 className="font-serif text-4xl text-stone-900 mt-2">Editors</h1>
                </div>
                <Button onClick={() => { setEditing("new"); setF(blank); }} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="add-editor">
                    <Plus className="w-4 h-4 mr-2" />Add editor
                </Button>
            </div>

            <div className="bg-amber-50 border border-amber-300 px-4 py-3 mb-6 text-sm text-amber-900 flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
                <p>
                    Editors can manage everything in the admin panel <strong>except passwords</strong>.
                    Only admins can create editor accounts, reset passwords, or change account status.
                </p>
            </div>

            {editing && (
                <div className="bg-white border border-stone-200 p-5 mb-6 space-y-3" data-testid="editor-form">
                    <div className="flex justify-between items-center">
                        <h2 className="font-serif text-2xl">{editing === "new" ? "New" : "Edit"} editor</h2>
                        <Button variant="ghost" onClick={() => { setEditing(null); setF(blank); }} data-testid="editor-form-close"><X className="w-4 h-4" /></Button>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <CF label="Full name *" v={f.name} on={(v) => setF({ ...f, name: v })} t="editor-name" />
                        <CF label="Email *" v={f.email} on={(v) => setF({ ...f, email: v })} type="email" disabled={editing !== "new"} t="editor-email" />
                        <CF label="Phone" v={f.phone} on={(v) => setF({ ...f, phone: v })} t="editor-phone" />
                        {editing === "new" && (
                            <CF label="Password (min 6) *" v={f.password} on={(v) => setF({ ...f, password: v })} type="password" t="editor-password" />
                        )}
                    </div>
                    <Button onClick={save} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="editor-save">
                        Save
                    </Button>
                </div>
            )}

            <div className="bg-white border border-stone-200 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-stone-50 border-b border-stone-200">
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Name</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Email</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Phone</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Active</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 ? (
                            <tr><td colSpan={5} className="p-8 text-center text-stone-500">No editor accounts yet.</td></tr>
                        ) : items.map(e => (
                            <tr key={e.id} className="border-b border-stone-100" data-testid={`editor-row-${e.id}`}>
                                <td className="p-3 font-medium">{e.name}</td>
                                <td className="p-3 text-stone-600">{e.email}</td>
                                <td className="p-3 text-stone-600">{e.phone || "—"}</td>
                                <td className="p-3">
                                    <Switch checked={e.active !== false} onCheckedChange={() => toggleActive(e)} data-testid={`editor-active-${e.id}`} />
                                </td>
                                <td className="p-3 text-right space-x-3">
                                    <button onClick={() => { setEditing(e.id); setF({ ...blank, ...e }); }} className="text-stone-700 underline text-xs" data-testid={`editor-edit-${e.id}`}>Edit</button>
                                    <button onClick={() => resetPw(e.id)} className="text-stone-700 inline-flex items-center text-xs" data-testid={`editor-reset-${e.id}`}><Key className="w-3.5 h-3.5 mr-1" />Reset password</button>
                                    <button onClick={() => del(e.id)} className="text-red-700" data-testid={`editor-del-${e.id}`}><Trash2 className="w-4 h-4" /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

const CF = ({ label, v, on, type = "text", disabled, t }) => (
    <div>
        <Label className="text-xs uppercase tracking-widest text-stone-500">{label}</Label>
        <Input type={type} value={v || ""} onChange={(e) => on(e.target.value)} disabled={disabled} className="rounded-none border-stone-300 mt-1" data-testid={t} />
    </div>
);
